import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import Hashids from 'hashids';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { Button, Card } from 'reactstrap';

import { getApiErrorMessage, hasPermission } from '@/api/admin-access';
import {
  ACCOUNT_TYPE_OPTIONS,
  AccountType,
  type AccountTypeOption,
  type BusinessLink,
  createManagedUser,
  getManagedUser,
  type ManagedUserDetails,
  normalizeAccountType,
  searchBrands,
  searchVenues,
  sendManagedUserPasswordReset,
  updateManagedUser,
} from '@/api/users';
import Breadcrumb from '@/components/common/breadcrumb.component';
import { UserStore } from '@/stores';

const hashids = new Hashids('', 12);

interface BusinessOption {
  value: number;
  label: string;
}

interface UserFormState {
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone: string;
  active: boolean;
  privateProfile: boolean;
  postPublic: boolean;
  accountType: AccountType;
  brands: BusinessOption[];
  venues: BusinessOption[];
  sendPasswordReset: boolean;
}

const emptyForm: UserFormState = {
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  phone: '',
  active: true,
  privateProfile: false,
  postPublic: true,
  accountType: AccountType.BASIC,
  brands: [],
  venues: [],
  sendPasswordReset: true,
};

const toOption = (business: BusinessLink): BusinessOption => ({
  value: business.id,
  label: business.name,
});

const formFromUser = (user: ManagedUserDetails): UserFormState => {
  const brands = (user.claims || [])
    .filter((claim) => claim.brand)
    .map((claim) => toOption(claim.brand!));
  const venues = (user.claims || [])
    .filter((claim) => claim.venue)
    .map((claim) => toOption(claim.venue!));
  return {
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    displayName: user.display_name || '',
    email: user.email || '',
    phone: user.phone || '',
    active: user.active !== false,
    privateProfile: Boolean(user.private_profile),
    postPublic: user.post_public !== false,
    accountType:
      brands.length || venues.length
        ? AccountType.BUSINESS
        : normalizeAccountType(user.acct_type),
    brands,
    venues,
    sendPasswordReset: false,
  };
};

const UserEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const access = UserStore.useState((state) => state.access);
  const canWrite = hasPermission(access, 'users', 'write');
  const isNew = params.id === 'new';
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [user, setUser] = useState<ManagedUserDetails | null>(
    location.state?.user || null
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const userId = useMemo(() => {
    if (isNew || !params.id) return null;
    const decoded = hashids.decode(params.id)[0];
    const numericId = Number(decoded);
    return Number.isInteger(numericId) ? numericId : null;
  }, [isNew, params.id]);

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setLoading(false);
      return;
    }

    if (!userId) {
      toast.error('This user link is invalid.');
      navigate('/users/manager', { replace: true });
      return;
    }

    setLoading(true);
    getManagedUser(userId)
      .then((loadedUser) => {
        setUser(loadedUser);
        setForm(formFromUser(loadedUser));
      })
      .catch((error) => toast.error(getApiErrorMessage(error)))
      .finally(() => setLoading(false));
  }, [isNew, navigate, userId]);

  const setField = <K extends keyof UserFormState>(
    field: K,
    value: UserFormState[K]
  ) => setForm((current) => ({ ...current, [field]: value }));

  const setBusinesses = (
    field: 'brands' | 'venues',
    value: readonly BusinessOption[]
  ) =>
    setForm((current) => ({
      ...current,
      [field]: [...value],
      accountType:
        value.length ||
        (field === 'brands' ? current.venues.length : current.brands.length)
          ? AccountType.BUSINESS
          : current.accountType,
    }));

  const loadBrands = useCallback(async (query: string) => {
    const brands = await searchBrands(query);
    return brands.map(toOption);
  }, []);

  const loadVenues = useCallback(async (query: string) => {
    const venues = await searchVenues(query);
    return venues.map(toOption);
  }, []);

  const validate = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('First and last name are required.');
      return false;
    }
    if (!form.email.trim()) {
      toast.error('Email is required.');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      display_name: form.displayName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      active: form.active,
      private_profile: form.privateProfile,
      post_public: form.postPublic,
      acct_type:
        form.brands.length || form.venues.length
          ? AccountType.BUSINESS
          : form.accountType,
      brand_ids: form.brands.map((brand) => brand.value),
      venue_ids: form.venues.map((venue) => venue.value),
      ...(isNew ? { send_password_reset: form.sendPasswordReset } : undefined),
    };

    try {
      const result = isNew
        ? await createManagedUser(payload)
        : await updateManagedUser(userId!, payload);
      setUser(result.user);
      setForm(formFromUser(result.user));
      let successMessage = 'User updated.';
      if (isNew && result.password_reset_sent) {
        successMessage = 'User created and password setup email sent.';
      } else if (isNew && form.sendPasswordReset) {
        successMessage =
          'User created, but the password setup email could not be sent.';
      } else if (isNew) {
        successMessage = 'User created.';
      }
      if (isNew && form.sendPasswordReset && !result.password_reset_sent) {
        toast.warning(successMessage);
      } else {
        toast.success(successMessage);
      }
      if (isNew) {
        const routeId =
          result.user.hash_id || hashids.encode(result.user.user_id);
        navigate(`/users/editor/${routeId}`, {
          replace: true,
          state: { user: result.user },
        });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!userId) return;
    setSendingReset(true);
    try {
      await sendManagedUserPasswordReset(userId);
      toast.success('Password reset email sent.');
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Breadcrumb title="User Editor" label="Editor" parent="Users" />
        <div className="container-fluid text-center" style={{ padding: 80 }}>
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  let statusBadge = form.active ? 'Active' : 'Inactive';
  if (isNew) statusBadge = 'New';
  let saveLabel = 'Save Changes';
  if (isNew) saveLabel = 'Create User';
  if (saving) saveLabel = 'Saving…';

  return (
    <div>
      <Breadcrumb
        title={isNew ? 'Create User' : user?.full_name || 'Edit User'}
        label="Editor"
        parent="Users"
        badge={statusBadge}
        badgeColor={form.active ? 'success' : 'default'}
      />

      <div className="container-fluid" style={{ maxWidth: 1050 }}>
        <Card style={{ marginTop: 20, padding: 20 }}>
          <h5 style={{ marginBottom: 20 }}>Account details</h5>
          <div className="row">
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-first-name">First name</label>
              <input
                id="user-first-name"
                className="form-control"
                value={form.firstName}
                onChange={(event) => setField('firstName', event.target.value)}
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-last-name">Last name</label>
              <input
                id="user-last-name"
                className="form-control"
                value={form.lastName}
                onChange={(event) => setField('lastName', event.target.value)}
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-email">Email</label>
              <input
                id="user-email"
                className="form-control"
                type="email"
                value={form.email}
                onChange={(event) => setField('email', event.target.value)}
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-phone">Phone</label>
              <input
                id="user-phone"
                className="form-control"
                type="tel"
                value={form.phone}
                onChange={(event) => setField('phone', event.target.value)}
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-display-name">Display name</label>
              <input
                id="user-display-name"
                className="form-control"
                value={form.displayName}
                onChange={(event) =>
                  setField('displayName', event.target.value)
                }
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-account-type">Account type</label>
              <Select<AccountTypeOption, false>
                inputId="user-account-type"
                options={ACCOUNT_TYPE_OPTIONS}
                value={
                  ACCOUNT_TYPE_OPTIONS.find(
                    (option) => option.value === form.accountType
                  ) || null
                }
                isDisabled={Boolean(form.brands.length || form.venues.length)}
                onChange={(option) =>
                  setField('accountType', option?.value ?? AccountType.BASIC)
                }
              />
              {(form.brands.length > 0 || form.venues.length > 0) && (
                <small className="text-muted">
                  Linked businesses make this a business account.
                </small>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 24px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.active}
                  onChange={(event) => setField('active', event.target.checked)}
                />
              }
              label="Active account"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.privateProfile}
                  onChange={(event) =>
                    setField('privateProfile', event.target.checked)
                  }
                />
              }
              label="Private profile"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.postPublic}
                  onChange={(event) =>
                    setField('postPublic', event.target.checked)
                  }
                />
              }
              label="Posts are public by default"
            />
          </div>
        </Card>

        <Card style={{ marginTop: 16, padding: 20 }}>
          <h5>Business access</h5>
          <p className="text-muted">
            Linking a brand or venue creates an approved claim for this user,
            allowing you to claim the profile on their behalf.
          </p>
          <div className="row">
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-brands">Brands</label>
              <AsyncSelect
                inputId="user-brands"
                isMulti
                isClearable
                cacheOptions
                closeMenuOnSelect={false}
                defaultOptions
                value={form.brands}
                loadOptions={loadBrands}
                onChange={(value) => setBusinesses('brands', value)}
                placeholder="Search brands..."
              />
            </div>
            <div className="col-md-6" style={{ marginBottom: 16 }}>
              <label htmlFor="user-venues">Venues</label>
              <AsyncSelect
                inputId="user-venues"
                isMulti
                isClearable
                cacheOptions
                closeMenuOnSelect={false}
                defaultOptions
                value={form.venues}
                loadOptions={loadVenues}
                onChange={(value) => setBusinesses('venues', value)}
                placeholder="Search venues..."
              />
            </div>
          </div>
        </Card>

        <Card style={{ marginTop: 16, padding: 20 }}>
          <h5>Password</h5>
          {isNew ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.sendPasswordReset}
                    disabled={!canWrite}
                    onChange={(event) =>
                      setField('sendPasswordReset', event.target.checked)
                    }
                  />
                }
                label="Send Password Reset"
              />
              <Alert severity="info" style={{ marginTop: 8 }}>
                The user will receive a secure link to choose their password.
              </Alert>
            </>
          ) : (
            <div>
              <p className="text-muted">
                Send a fresh password-reset link to {form.email}.
              </p>
              <Button
                outline
                color="secondary"
                disabled={!canWrite || sendingReset || !form.email}
                onClick={sendPasswordReset}
              >
                {sendingReset ? 'Sending…' : 'Send Password Reset'}
              </Button>
            </div>
          )}
        </Card>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '20px 0 36px',
          }}
        >
          <Button outline disabled={saving} onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button color="primary" disabled={!canWrite || saving} onClick={save}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserEditor;
